<template>
  <div class="relative">
    <ContextMenu close-on-outside-click default-position="bottom-left">
      <template #facing="{ toggle }">
        <NamiButton
          class="!text-base"
          :buttonType="followData.status ? 'secondary' : 'primary'"
          :disabled="followData.initialFetchPending"
          block
          @buttonClick="
            () => {
              if (followData.status) toggle();
              else updateFollow('reading', true);
            }
          "
          :pill
          ref="followButton"
        >
          <span
            v-if="followData.status"
            class="truncate flex items-center gap-2"
            :class="{ 'px-1': pill }"
          >
            <IconBellRinging v-if="followData.notification" />
            <IconBellOff v-else />
            <span class="truncate">{{
              $t(`entity.title.follow.reading_status.${followData.status}`)
            }}</span>
            <IconChevronDown />
          </span>
          <span
            v-else
            class="truncate flex items-center gap-1"
            :class="{ 'px-1': pill }"
          >
            <slot name="followText">
              <IconBookmark />
              {{
                followText?.follow ||
                $t("components.TitleFollowButton.button.follow")
              }}
            </slot>
          </span>
        </NamiButton>
      </template>
      <template #menu="{ close }">
        <div
          class="bg-white dark:bg-neutral-800 grid grid-cols-[auto_1fr] p-4 gap-2 rounded-md items-center"
          :style="{ minWidth: buttonSize > 0 ? `${buttonSize}px` : undefined }"
        >
          <span class="whitespace-nowrap">Notifications</span>
          <NamiSwitch
            class="ml-auto"
            :model-value="followData.notification"
            @update:model-value="(v) => updateNotification(v)"
          />
          <span class="whitespace-nowrap">Status</span>
          <ContextMenuList
            :options="readingStatuses"
            class="ml-auto"
            v-show="followData.status"
          >
            <template v-slot="{ isOpen, open, close }">
              <NamiButton
                button-type="secondary"
                small
                @button-click="() => (isOpen ? close() : open())"
              >
                <span class="px-1 whitespace-nowrap">{{
                  $t(`entity.title.follow.reading_status.${followData.status}`)
                }}</span>
              </NamiButton>
            </template>
          </ContextMenuList>
          <NamiButton
            class="col-span-2"
            button-type="danger"
            text
            small
            @button-click="() => (close(), updateStatus(null))"
          >
            <slot name="unfollowText"
              >{{
                followText?.unfollow ||
                $t("components.TitleFollowButton.button.unfollow")
              }}
            </slot>
          </NamiButton>
        </div>
      </template>
    </ContextMenu>
  </div>
</template>

<script setup lang="ts">
import {
  IconBellOff,
  IconBellRinging,
  IconChevronDown,
  IconBookmark,
} from "@tabler/icons-vue";
import type { TitleReadingStatus } from "~/src/api";
import { NamiButton } from "#components";

const nuxtApp = useNuxtApp();
const appStore = nuxtApp.$app();
const { t } = useI18n();

const props = defineProps<{
  titleId: string;
  pill?: boolean;
  followText?: {
    follow?: string;
    unfollow?: string;
  };
}>();

const readingStatuses = [
  "reading",
  "plan_to_read",
  "on_hold",
  "completed",
  "dropped",
].map((s) => ({
  name: t(`entity.title.follow.reading_status.${s}`),
  action: () => {
    updateFollow(s as TitleReadingStatus, followData.value.notification);
  },
}));

const {
  followData,
  updateFollow,
  updateNotification,
  updateStatus,
  initialize,
} = useTitleFollow(props.titleId, (err) => {
  const { title, detail } = resolveError(err);
  appStore?.notify({
    preset: "error.plain",
    title,
    detail,
    timer: 3000,
  });
});

const followButton = ref<InstanceType<typeof NamiButton>>();
const buttonSize = ref(0);
let observer: ResizeObserver | undefined;

onMounted(() => {
  if (followButton.value) {
    observer = new ResizeObserver(() => {
      buttonSize.value = followButton.value?.$el.clientWidth;
    });
    observer.observe(followButton.value.$el);
  }

  initialize();
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});

defineExpose({
  initialize,
});
</script>
